import Dialog from '@/components/_custom/Dialog/Dialog.tsx'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader.tsx'
import DialogBody from '@/components/_custom/DialogBody/DialogBody.tsx'
import { FunctionComponent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UPDATE_MEAL_DETAIL_QUANTITY } from '@/modules/API/meal/daycareSettingsHandler.ts'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import { useMutation } from '@apollo/client'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select.ts'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple.tsx'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique.tsx'
import { MealAccessibilitySection, MealHabit, MealMenu, MealTexture, MealType } from '@/types/Meal.ts'
import styles from './UpdateMealQuantityDialog.module.scss'

type UpdateMealQuantityDialogProps = {
    open: boolean
    handleClose: () => void
    refetch: () => void
    daycareId: number
    date: string
    menus: MealMenu[]
    textures: MealTexture[]
    habits: MealHabit[]
    sections: { id: number; name: string }[]
    isRpeOrLaep: boolean
    selectedMealTypes: string[]
    mealAccessibilitySections: MealAccessibilitySection[]
}

const UpdateMealQuantityDialog: FunctionComponent<UpdateMealQuantityDialogProps> = ({
    open,
    handleClose,
    refetch,
    daycareId,
    date,
    menus,
    textures,
    habits,
    sections,
    isRpeOrLaep,
    selectedMealTypes,
    mealAccessibilitySections
}: UpdateMealQuantityDialogProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const hasOnlyOneSection: boolean = sections.length === 1
    const defaultSectionId: string = hasOnlyOneSection ? sections[0].id.toString() : ''

    const [selectedSectionId, setSelectedSectionId] = useState<string>(defaultSectionId)
    const [selectedMealType, setSelectedMealType] = useState<string>('')

    function handleSectionChange(sectionId: string): void {
        setSelectedSectionId(sectionId)
        setSelectedMealType('')
    }
    const [selectedMenuIds, setSelectedMenuIds] = useState<string[]>([])
    const [selectedTextureIds, setSelectedTextureIds] = useState<string[]>([])
    const [selectedHabitIds, setSelectedHabitIds] = useState<string[]>([])

    const [updateMealDetailQuantity, { loading }] = useMutation(UPDATE_MEAL_DETAIL_QUANTITY, {
        onCompleted: () => {
            refetch()
            resetAndClose()
            showNotificationSuccess(t('mealAdded'))
        },
        onError: () => {
            resetAndClose()
            showNotificationError(t('mealAddedError'))
        }
    })

    function resetAndClose(): void {
        setSelectedSectionId(defaultSectionId)
        setSelectedMealType('')
        setSelectedMenuIds([])
        setSelectedTextureIds([])
        setSelectedHabitIds([])
        handleClose()
    }

    const mealTypeTranslations: Record<string, string> = {
        [MealType.breakfast]: t('mealTypeBreakfast'),
        [MealType.lunch]: t('mealTypeLunch'),
        [MealType.snack]: t('mealTypeSnack'),
        [MealType.dinner]: t('mealTypeDinner')
    }

    const availableMealTypesForSection: string[] = selectedSectionId
        ? [
              ...new Set(
                  mealAccessibilitySections
                      .filter(
                          (a: MealAccessibilitySection): boolean =>
                              a.sectionId === Number(selectedSectionId) && selectedMealTypes.includes(a.type)
                      )
                      .map((a: MealAccessibilitySection): string => a.type)
              )
          ]
        : selectedMealTypes

    const hasOnlyOneMealTypeForSection: boolean = availableMealTypesForSection.length === 1

    const effectiveMealType: string = hasOnlyOneMealTypeForSection ? availableMealTypesForSection[0] : selectedMealType

    function handleSubmit(): void {
        updateMealDetailQuantity({
            variables: {
                daycareId,
                sectionIds: [Number(selectedSectionId)],
                date,
                menuIds: selectedMenuIds.map(Number),
                textureIds: selectedTextureIds.map(Number),
                habitIds: selectedHabitIds.map(Number),
                action: 'increase',
                mealType: effectiveMealType
            }
        })
    }

    const mealTypeOptions: SelectOptionsDeprecated = [
        {
            options: availableMealTypesForSection.map(
                (type): SelectOptionDeprecated => ({
                    name: mealTypeTranslations[type] || type,
                    value: type
                })
            )
        }
    ]

    const sectionOptions: SelectOptionsDeprecated = [
        {
            options: sections.map(
                (section): SelectOptionDeprecated => ({
                    name: section.name,
                    value: section.id.toString()
                })
            )
        }
    ]

    const menuOptions: SelectOptionsDeprecated = [
        {
            options: menus.map(
                (menu: MealMenu): SelectOptionDeprecated => ({
                    name: menu.menu,
                    value: menu.id.toString()
                })
            )
        }
    ]

    const textureOptions: SelectOptionsDeprecated = [
        {
            options: textures.map(
                (texture: MealTexture): SelectOptionDeprecated => ({
                    name: texture.texture,
                    value: texture.id.toString()
                })
            )
        }
    ]

    const habitOptions: SelectOptionsDeprecated = [
        {
            options: habits.map(
                (habit: MealHabit): SelectOptionDeprecated => ({
                    name: habit.habit,
                    value: habit.id.toString()
                })
            )
        }
    ]

    return (
        <Dialog open={open} handleClose={resetAndClose}>
            <DialogHeader title={t('addMeal')} />
            <DialogBody className={styles.DialogBody}>
                {!hasOnlyOneSection && (
                    <SelectUnique
                        label={isRpeOrLaep ? t('sector') : t('section')}
                        selectOptions={sectionOptions}
                        selectValue={selectedSectionId}
                        setSelectValue={handleSectionChange}
                        className={styles.UserInput}
                        fullWidth={true}
                    />
                )}
                {selectedSectionId && !hasOnlyOneMealTypeForSection && (
                    <SelectUnique
                        label={t('mealType')}
                        selectOptions={mealTypeOptions}
                        selectValue={selectedMealType}
                        setSelectValue={setSelectedMealType}
                        className={styles.UserInput}
                        fullWidth={true}
                    />
                )}
                {menus.length > 0 && (
                    <SelectMultiple
                        label={t('menus')}
                        selectOptions={menuOptions}
                        selectValues={selectedMenuIds}
                        setSelectValues={setSelectedMenuIds}
                        className={styles.UserInput}
                        fullWidth={true}
                    />
                )}
                {textures.length > 0 && (
                    <SelectMultiple
                        label={t('textures')}
                        selectOptions={textureOptions}
                        selectValues={selectedTextureIds}
                        setSelectValues={setSelectedTextureIds}
                        className={styles.UserInput}
                        fullWidth={true}
                    />
                )}
                {habits.length > 0 && (
                    <SelectMultiple
                        label={t('habits')}
                        selectOptions={habitOptions}
                        selectValues={selectedHabitIds}
                        setSelectValues={setSelectedHabitIds}
                        className={styles.UserInput}
                        fullWidth={true}
                    />
                )}
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type='close' onClick={resetAndClose} className={styles.Button} />
                    <ButtonTextIcon
                        type='add'
                        disabled={loading || !selectedSectionId || !effectiveMealType}
                        onClick={handleSubmit}
                        className={styles.Button}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default UpdateMealQuantityDialog
